// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { UploadingFile } from '@/components/file-drop-zone';
import { uploadFile } from '@/file/api/upload-file';
import { captureException } from '@sentry/react';
import axios from 'axios';
import { useCallback, useMemo, useState } from 'react';

/**
 * Return type for the usePurchaseOrderFileUploads hook.
 */
interface UsePurchaseOrderFileUploadsReturn {
  /** The list of files currently being uploaded or already uploaded */
  files: UploadingFile[];
  /** Adds files to the upload queue and starts uploading them immediately */
  addFiles: (files: File[]) => void;
  /** Removes a file from the list by its local ID */
  removeFile: (localId: string) => void;
  /** Whether any file has a failed upload status */
  hasFailedUploads: boolean;
  /** Whether any file is currently uploading */
  isAnyUploading: boolean;
  /** Returns an array of server file IDs for successfully uploaded files */
  getCompletedFileIds: () => string[];
  /** Resets the file list to empty */
  reset: () => void;
}

/**
 * Hook to manage file uploads during purchase order creation.
 * Files are uploaded immediately when added, and their IDs are stored
 * for linking to the purchase order when it's created.
 *
 * Note: This hook uploads files without an entityId since the PO doesn't exist yet.
 * The files will be linked to the PO after creation using the fileIds parameter.
 */
export function usePurchaseOrderFileUploads(): UsePurchaseOrderFileUploadsReturn {
  const [files, setFiles] = useState<UploadingFile[]>([]);

  const addFiles = useCallback((newFiles: File[]) => {
    // Create uploading file objects for each new file
    const uploadingFiles: UploadingFile[] = newFiles.map((file) => ({
      localId: crypto.randomUUID(),
      file,
      status: 'uploading' as const,
    }));

    // Add to state
    setFiles((prevFiles) => [...prevFiles, ...uploadingFiles]);

    // Start uploading each file
    for (const uploadingFile of uploadingFiles) {
      uploadFile(uploadingFile.file, {
        context: 'PURCHASE_ORDER_FILE',
        isPublic: false,
      })
        .then((response) => {
          // Update file status to complete with server info
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.localId === uploadingFile.localId
                ? {
                    ...f,
                    status: 'complete' as const,
                    serverKey: response.fileKey,
                    serverFileId: response.fileId,
                  }
                : f
            )
          );
        })
        .catch((error: unknown) => {
          let errorMessage = 'Upload failed';
          if (axios.isAxiosError(error)) {
            const apiError = handleAPIError(error);
            errorMessage = getUserFacingErrorMessage(apiError);
            if (
              errorMessage === SUPPORT_FALLBACK_ERROR_MESSAGE &&
              !apiError.sentToSentry
            ) {
              captureException(error);
            }
          } else {
            captureException(error);
          }
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.localId === uploadingFile.localId
                ? {
                    ...f,
                    status: 'error' as const,
                    error: errorMessage,
                  }
                : f
            )
          );
        });
    }
  }, []);

  const removeFile = useCallback((localId: string) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.localId !== localId));
  }, []);

  const hasFailedUploads = useMemo(
    () => files.some((f) => f.status === 'error'),
    [files]
  );

  const isAnyUploading = useMemo(
    () => files.some((f) => f.status === 'uploading'),
    [files]
  );

  const getCompletedFileIds = useCallback(() => {
    return files
      .filter(
        (f): f is UploadingFile & { serverFileId: string } =>
          f.status === 'complete' && typeof f.serverFileId === 'string'
      )
      .map((f) => f.serverFileId);
  }, [files]);

  const reset = useCallback(() => {
    setFiles([]);
  }, []);

  return {
    files,
    addFiles,
    removeFile,
    hasFailedUploads,
    isAnyUploading,
    getCompletedFileIds,
    reset,
  };
}
